import { ref, computed, watch, onMounted, onBeforeUnmount } from "vue";
import { useStore } from "vuex";
import moment from "moment";
import engine from "../core/DataApi";

// ─────────────────────────────────────────────────────────────────────────
// Module « SAV Web » — suivi des appels / tickets clients (logique).
// Tickets : table FileMaker WEB_SAV. Fil d'échanges : WEB_SAV_ECHANGES.
// Clients : layout « Clients » (lecture seule).
// ─────────────────────────────────────────────────────────────────────────

// Valeurs proposées par défaut ; les valeurs trouvées dans les tickets s'y
// ajoutent automatiquement. 👉 Édite ces listes pour changer les choix.
export const DEFAULT_STATUTS = ["Ouvert", "En cours", "En attente client", "Résolu", "Clos"];
export const DEFAULT_FORMES = ["Appel entrant", "Appel sortant", "Email", "Autre"];
// Statuts considérés comme « terminés » (exclus de la vue « À traiter »).
const CLOSED_STATUTS = ["resolu", "clos", "cloture", "ferme", "termine", "annule"];

// Types d'échanges saisissables + types « système » (générés automatiquement).
export const ECHANGE_TYPES = [
    { value: "note", label: "Note", icon: "mdi-note-text-outline", color: "#607D8B" },
    { value: "appel_entrant", label: "Appel entrant", icon: "mdi-phone-incoming", color: "#43A047" },
    { value: "appel_sortant", label: "Appel sortant", icon: "mdi-phone-outgoing", color: "#1E88E5" },
    { value: "email", label: "Email", icon: "mdi-email-outline", color: "#8E24AA" },
];
const SYSTEM_TYPES = {
    statut: { label: "Statut", icon: "mdi-swap-horizontal", color: "#78909C" },
    assignation: { label: "Prise en charge", icon: "mdi-account-arrow-right-outline", color: "#78909C" },
    modification: { label: "Modification", icon: "mdi-pencil-outline", color: "#78909C" },
};

export const UNASSIGNED = "(Non assigné)";
export const PERIODES = [
    { title: "Aujourd'hui", value: "today" },
    { title: "7 derniers jours", value: "7d" },
    { title: "30 derniers jours", value: "30d" },
    { title: "90 derniers jours", value: "90d" },
];
const PERIODE_DAYS = { "7d": 7, "30d": 30, "90d": 90 };

const TICKETS_LIMIT = 10000;        // plafond (non clos + clos depuis 30 j, côté serveur)
const SEARCH_DEBOUNCE_MS = 500;     // délai avant recherche dans l'ensemble des données
const AUTO_REFRESH_MS = 30000;      // actualisation automatique légère
const FULL_RELOAD_EVERY = 10;       // 1 rechargement complet toutes les 10 actualisations
const INCREMENTAL_MARGIN_MS = 120000; // marge de recouvrement de l'incrémental

// ── Helpers purs ──────────────────────────────────────────────────────────
export const norm = (s) => (s === undefined || s === null ? "" : String(s))
    .normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
export const digits = (s) => (s === undefined || s === null ? "" : String(s)).replace(/\D/g, "");
const txt = (v) => (v === undefined || v === null ? "" : String(v));
// FileMaker stocke les retours à la ligne en \r (¶) → \n pour l'affichage.
const lines = (v) => txt(v).replace(/\r\n?/g, "\n");

// Expéditeurs masqués par défaut (notifications automatiques). 👉 Adresse
// complète, ou domaine entier en commençant par « @ » (ex. "@status-ovhcloud.com").
export const HIDDEN_SENDERS = [
    "no-reply@status-ovhcloud.com",
    "no_reply@accen.ovh",
    "proxmox@accen.fr",
];
const HIDDEN_LIST = HIDDEN_SENDERS.map((s) => norm(s)).filter(Boolean);
// Entrée de HIDDEN_SENDERS (normalisée) qui correspond à l'expéditeur du ticket, "" sinon.
export const hiddenEntryOf = (t) => {
    const v = norm(t && t.mail_expediteur);
    if (!v) return "";
    const m = v.match(/[^\s<>"',;]+@[^\s<>"',;]+/);
    const email = m ? m[0] : v;
    return HIDDEN_LIST.find((h) => (h.charAt(0) === "@" ? email.slice(-h.length) === h : email === h)) || "";
};
export const isHiddenSender = (t) => !!hiddenEntryOf(t);

export const isClosed = (statut) => CLOSED_STATUTS.indexOf(norm(statut).replace(/\s+/g, "")) !== -1;
// « En attente client » : la balle est chez le client → pas compté en retard.
const isWaiting = (statut) => norm(statut).indexOf("attente") !== -1;
const LATE_MS = 24 * 3600000;   // en retard au-delà de 24 h sans résolution
const WARN_MS = 3600000;        // à surveiller au-delà d'1 h

// Horodatages FileMaker : l'API Data renvoie MM/JJ/AAAA (comme pour WEB_LOGS).
const FM_FORMATS = [
    "MM/DD/YYYY HH:mm:ss", "MM/DD/YYYY HH:mm", "MM/DD/YYYY",
    "DD/MM/YYYY HH:mm:ss", "DD/MM/YYYY HH:mm", "DD/MM/YYYY",
    "YYYY-MM-DD HH:mm:ss", "YYYY-MM-DDTHH:mm:ss", "YYYY-MM-DD",
];
export const parseFm = (v) => {
    if (v === undefined || v === null || v === "") return 0;
    const m = moment(String(v), FM_FORMATS, true);
    return m.isValid() ? m.valueOf() : 0;
};

// Champ « Horodatage de création/modification » repéré par tokens (accents,
// espaces insécables… ignorés), comme dans le module Logs Web.
const pickTs = (fd, kind) => {
    const k = Object.keys(fd).find((key) => {
        const n = norm(key);
        return n.indexOf("horodat") !== -1 && (kind === "modif" ? n.indexOf("modif") !== -1 : n.indexOf("crea") !== -1);
    });
    return k ? fd[k] : "";
};
const pickPk = (fd) => {
    const k = Object.keys(fd).find((key) => norm(key).replace(/\s+/g, "") === "cleprimaire");
    return k ? txt(fd[k]).trim() : "";
};

const extractRows = (data) => {
    if (!data) return [];
    const candidates = [data.data && data.data.data, data.data, data];
    for (let i = 0; i < candidates.length; i++) {
        if (Array.isArray(candidates[i])) return candidates[i];
    }
    return [];
};

export const mapTicket = (row) => {
    const fd = (row && row.fieldData) || {};
    const recordId = row && row.recordId !== undefined && row.recordId !== null ? String(row.recordId) : null;
    const cree = pickTs(fd, "crea");
    const modifie = pickTs(fd, "modif");
    const creeMs = parseFm(cree);
    const modifMs = parseFm(modifie) || creeMs;
    return {
        id: pickPk(fd) || (recordId ? "r" + recordId : ""),
        recordId: recordId,
        societe: txt(fd.societe).trim(),
        client: txt(fd.client).trim(),
        telephone: txt(fd.telephone).trim(),
        // Tickets créés depuis un email (champs du layout WEB_SAV).
        mail_expediteur: txt(fd.mail_expediteur || fd.email_expediteur).trim(),
        mail_expediteur_nom: txt(fd.mail_expediteur_nom || fd.email_expediteur_nom).trim(),
        commentaire: lines(fd.commentaire),
        ia_resume: lines(fd.ia_resume).trim(), // résumé IA rempli dans FileMaker
        forme: txt(fd.forme).trim(),
        statut: txt(fd.statut).trim(),
        user_interne: txt(fd.user_interne).trim(),
        derniere_activite: txt(fd.derniere_activite),
        cree: cree,
        modifieRaw: txt(modifie),
        creeMs: creeMs,
        modifMs: modifMs,
        creeSort: creeMs,   // colonnes triables du tableau
        activite: modifMs,
    };
};

export const mapEchange = (row) => {
    const fd = (row && row.fieldData) || {};
    const recordId = row && row.recordId !== undefined && row.recordId !== null ? String(row.recordId) : null;
    const cree = pickTs(fd, "crea");
    return {
        id: pickPk(fd) || (recordId ? "e" + recordId : ""),
        recordId: recordId,
        ticketId: txt(fd.id_ticket).trim(),
        auteur: txt(fd.auteur).trim(),
        type: txt(fd.type).trim() || "note",
        message: lines(fd.message),
        cree: cree,
        creeMs: parseFm(cree),
    };
};

const sortTickets = (list) => list.slice().sort((a, b) => (b.modifMs - a.modifMs) || (b.creeMs - a.creeMs));

// Valeurs distinctes (sans doublon de casse/accents), dans l'ordre d'arrivée.
const uniqNorm = (arr) => {
    const seen = {};
    const out = [];
    arr.forEach((v) => {
        const s = txt(v).trim();
        const k = norm(s);
        if (!s || seen[k]) return;
        seen[k] = true;
        out.push(s);
    });
    return out;
};
const byFr = (a, b) => a.localeCompare(b, "fr", { sensitivity: "base" });

export default function () {
    const store = useStore();

    // Utilisateur connecté (fiche utilisateur de l'app : user_name).
    const currentUser = computed(() => {
        const u = (store && store.state && store.state.user) || {};
        return txt(u.user_name || u.user_email || u.user_login).trim();
    });

    // ── État ──────────────────────────────────────────────────────────────
    const tickets = ref([]);
    const isLoading = ref(false);     // 1er chargement (écran de chargement)
    const isRefreshing = ref(false);  // actualisation silencieuse
    const loadError = ref(false);
    const lastSync = ref(null);
    const newIds = ref([]);           // tickets arrivés/modifiés à la dernière actualisation
    const nowTick = ref(Date.now());  // fait vivre les « il y a 5 min »

    const clients = ref([]);
    const isClientsLoading = ref(false);
    const clientsInfo = ref({ fields: [], mapping: {} });

    const selectedId = ref(null);
    const echanges = ref([]);
    const isEchangesLoading = ref(false);
    const isSaving = ref(false);      // création / modification de ticket
    const isSending = ref(false);     // ajout d'un échange

    const snackbar = ref({ show: false, text: "", color: "" });
    const showMessage = (text, color) => { snackbar.value = { show: true, text: text, color: color }; };
    // Ajoute la raison renvoyée par FileMaker (ex. « FileMaker 105 : Layout is missing »).
    const withDetail = (text, data) => (data && data.detail ? text + " — " + data.detail : text);

    const emptyFilters = () => ({
        search: "",
        vue: "mes", // vue à l'ouverture : a_traiter | mes | retard | tous
        statut: [],
        forme: [],
        user: null,
        societe: null,
        periode: null,
        global: false,    // « Rechercher dans l'ensemble des données »
        shownSenders: [],  // expéditeurs masqués que l'on a choisi d'afficher (entrées normalisées)
    });
    const filters = ref(emptyFilters());
    const resetFilters = () => { filters.value = emptyFilters(); };

    // ── Chargement des tickets ────────────────────────────────────────────
    let refreshCount = 0;
    // Actualisation demandée pendant qu'une autre était en cours (ex. juste
    // après un enregistrement) : on la rejoue dès la fin de la première, sinon
    // une réponse partie AVANT l'enregistrement pourrait afficher l'ancienne
    // valeur jusqu'à la prochaine actualisation (30 s).
    let refreshPending = false;
    const flushPending = () => {
        if (!refreshPending) return;
        refreshPending = false;
        setTimeout(() => refreshTickets(), 0);
    };

    // Ids ajoutés ou modifiés entre deux listes (pour le surlignage).
    const diffIds = (oldList, newList) => {
        const before = {};
        oldList.forEach((t) => { before[t.id] = t.modifieRaw; });
        return newList.filter((t) => before[t.id] === undefined || before[t.id] !== t.modifieRaw).map((t) => t.id);
    };

    const afterChange = (changed) => {
        // Les surlignés restent jusqu'au prochain lot de changements.
        if (changed.length) newIds.value = changed;
        // Le ticket ouvert a bougé (autre utilisateur) → on recharge son fil.
        if (selectedId.value && changed.indexOf(selectedId.value) !== -1) loadEchanges(selectedId.value, true);
    };

    const loadTickets = (opts) => {
        const silent = !!(opts && opts.silent);
        if (silent) isRefreshing.value = true;
        else { isLoading.value = true; loadError.value = false; }
        engine.get_tickets({ limit: TICKETS_LIMIT }, (data) => {
            isLoading.value = false;
            isRefreshing.value = false;
            if (data && data.code === 0) {
                const list = sortTickets(extractRows(data).map(mapTicket).filter((t) => t.id));
                const changed = silent ? diffIds(tickets.value, list) : [];
                tickets.value = list;
                refreshCount = 0;
                lastSync.value = new Date();
                loadError.value = false;
                afterChange(changed);
                if (opts && typeof opts.then === "function") opts.then();
            } else if (!silent) {
                loadError.value = true;
            }
            flushPending();
        });
    };

    // Horodatage FileMaker à partir duquel demander les modifications : le plus
    // récent connu, moins une marge (dans la même « heure FileMaker »).
    const modifiedSince = () => {
        let max = 0;
        tickets.value.forEach((t) => { if (t.modifMs > max) max = t.modifMs; });
        return max ? moment(max - INCREMENTAL_MARGIN_MS).format("MM/DD/YYYY HH:mm:ss") : null;
    };

    const mergeTickets = (fresh) => {
        if (!fresh.length) return;
        const byId = {};
        tickets.value.forEach((t) => { byId[t.id] = t; });
        const changed = diffIds(tickets.value, fresh);
        fresh.forEach((t) => { byId[t.id] = t; });
        tickets.value = sortTickets(Object.keys(byId).map((k) => byId[k]));
        afterChange(changed);
    };

    // Actualisation incrémentale (seulement ce qui a changé) ; rechargement
    // complet de temps en temps pour rester parfaitement synchronisé.
    const refreshTickets = () => {
        if (isLoading.value || isRefreshing.value) { refreshPending = true; return; }
        refreshCount++;
        const since = modifiedSince();
        if (!since || refreshCount >= FULL_RELOAD_EVERY) { loadTickets({ silent: true }); return; }
        isRefreshing.value = true;
        engine.get_tickets({ modifiedSince: since, limit: 500 }, (data) => {
            isRefreshing.value = false;
            if (data && data.code === 0) {
                mergeTickets(extractRows(data).map(mapTicket).filter((t) => t.id));
                lastSync.value = new Date();
            }
            flushPending();
        });
    };

    // ── Fil d'échanges du ticket sélectionné ──────────────────────────────
    let echReq = 0;
    let echInFlight = null;
    const loadEchanges = (id, silent) => {
        if (!id) return;
        if (silent && echInFlight === id) return;
        const reqNo = ++echReq;
        echInFlight = id;
        if (!silent) { isEchangesLoading.value = true; echanges.value = []; }
        engine.get_echanges(id, (data) => {
            if (reqNo !== echReq) return; // réponse obsolète (autre ticket ouvert entre-temps)
            echInFlight = null;
            isEchangesLoading.value = false;
            if (data && data.code === 0) {
                if (selectedId.value !== id) return;
                echanges.value = extractRows(data).map(mapEchange)
                    .sort((a, b) => (a.creeMs - b.creeMs) || (Number(a.recordId) - Number(b.recordId)));
            } else if (!silent) {
                showMessage(withDetail("Impossible de charger les échanges du ticket.", data), "error");
            }
        });
    };

    // ── Recherche dans l'ensemble des données (toute la table FileMaker) ──
    const serverResults = ref([]);
    const isSearching = ref(false);
    const searchActive = computed(() => !!filters.value.global && norm(filters.value.search).length >= 2);
    let searchTimer = null;
    let searchReq = 0;
    const runServerSearch = () => {
        const q = txt(filters.value.search).trim();
        const reqNo = ++searchReq;
        isSearching.value = true;
        engine.get_tickets({ search: q }, (data) => {
            if (reqNo !== searchReq) return; // une frappe plus récente a relancé la recherche
            isSearching.value = false;
            if (data && data.code === 0) serverResults.value = sortTickets(extractRows(data).map(mapTicket).filter((t) => t.id));
            else { serverResults.value = []; showMessage(withDetail("Recherche impossible.", data), "error"); }
        });
    };
    watch(() => [filters.value.global, filters.value.search], () => {
        if (searchTimer) clearTimeout(searchTimer);
        if (!searchActive.value) { searchReq++; isSearching.value = false; serverResults.value = []; return; }
        searchTimer = setTimeout(runServerSearch, SEARCH_DEBOUNCE_MS);
    });
    // Liste affichée : tickets chargés, ou résultats de la recherche globale
    // (remplacés par la version chargée si elle existe, plus fraîche).
    const sourceTickets = computed(() => {
        if (!searchActive.value) return tickets.value;
        const fresh = {};
        tickets.value.forEach((t) => { fresh[t.id] = t; });
        return serverResults.value.map((t) => fresh[t.id] || t);
    });
    const findTicket = (id) => tickets.value.find((t) => t.id === id) || serverResults.value.find((t) => t.id === id) || null;

    const selectedTicket = computed(() => findTicket(selectedId.value));
    const selectTicket = (t) => {
        const id = t && t.id ? t.id : null;
        if (!id || id === selectedId.value) return;
        selectedId.value = id;
        loadEchanges(id, false);
    };
    const closeTicket = () => { selectedId.value = null; echanges.value = []; echReq++; echInFlight = null; };

    // ── Clients ───────────────────────────────────────────────────────────
    const loadClients = (force) => {
        isClientsLoading.value = true;
        engine.get_clients(!!force, (data) => {
            isClientsLoading.value = false;
            if (data && data.code === 0 && data.data) {
                clients.value = Array.isArray(data.data.clients) ? data.data.clients : [];
                clientsInfo.value = { fields: data.data.fields || [], mapping: data.data.mapping || {} };
                if (!clientsInfo.value.mapping.societe) {
                    console.warn("[sav-web] Layout Clients : champ « raison sociale » non reconnu.", clientsInfo.value);
                }
            }
        });
    };

    // Fiche client correspondant au ticket (par code, sinon par raison sociale).
    const findClient = (ticket) => {
        if (!ticket) return null;
        const code = norm(ticket.client);
        const soc = norm(ticket.societe);
        return clients.value.find((c) => code && norm(c.code) === code) ||
            clients.value.find((c) => soc && norm(c.societe) === soc) || null;
    };
    const selectedClient = computed(() => findClient(selectedTicket.value));

    // ── Options des listes ────────────────────────────────────────────────
    const statutOptions = computed(() => uniqNorm(DEFAULT_STATUTS.concat(tickets.value.map((t) => t.statut))));
    const formeOptions = computed(() => uniqNorm(DEFAULT_FORMES.concat(tickets.value.map((t) => t.forme))));
    const userOptions = computed(() => uniqNorm([currentUser.value]
        .concat(tickets.value.map((t) => t.user_interne), echanges.value.map((e) => e.auteur))).sort(byFr));
    const societeOptions = computed(() => uniqNorm(tickets.value.map((t) => t.societe)).sort(byFr));

    // ── Âge d'un ticket (depuis sa création, tant qu'il n'est pas résolu) ──
    const isLate = (t) => !!t && !!t.creeMs && !isClosed(t.statut) && !isWaiting(t.statut) && (nowTick.value - t.creeMs) > LATE_MS;
    const ageInfo = (t) => {
        if (!t || !t.creeMs || isClosed(t.statut)) return null;
        const ms = Math.max(0, nowTick.value - t.creeMs);
        const min = Math.floor(ms / 60000);
        let label;
        if (min < 60) label = min + " min";
        else if (min < 1440) { const m = min % 60; label = Math.floor(min / 60) + " h" + (m ? " " + (m < 10 ? "0" : "") + m : ""); }
        else label = Math.floor(min / 1440) + " j";
        const cls = isWaiting(t.statut) || ms <= WARN_MS ? "ok" : (ms <= LATE_MS ? "warn" : "late");
        return { label: label, cls: cls };
    };
    // N° de ticket court et lisible (8 premiers caractères de la clé).
    const shortId = (id) => txt(id).slice(0, 8).toUpperCase();

    // ── Filtres (côté client, instantanés) ────────────────────────────────
    // { list : tickets affichés, hidden : tickets masqués (expéditeur masqué) qui
    //   correspondraient sinon aux filtres → pastille « N mails masqués »,
    //   bySender : nb de tickets par expéditeur masqué → menu de la pastille }
    const filterResult = computed(() => {
        const f = filters.value;
        const me = norm(currentUser.value);
        const q = norm(f.search);
        const qd = digits(f.search);
        const statuts = (f.statut || []).map(norm);
        const formes = (f.forme || []).map(norm);
        let since = 0;
        if (f.periode === "today") since = moment().startOf("day").valueOf();
        else if (PERIODE_DAYS[f.periode]) since = Date.now() - PERIODE_DAYS[f.periode] * 86400000;
        const global = searchActive.value;
        const shown = f.shownSenders || [];
        let hidden = 0;
        const bySender = {}; // nb de tickets par expéditeur masqué (affichés ou non)
        const list = sourceTickets.value.filter((t) => {
            // Un filtre de statut explicite prime sur la vue (sinon « À traiter »
            // et « Mes tickets » masquent les tickets résolus/clos). En recherche
            // globale on montre aussi les tickets résolus/clos (historique).
            if (statuts.length) { if (statuts.indexOf(norm(t.statut)) === -1) return false; }
            else if (!global && f.vue !== "tous" && isClosed(t.statut)) return false;
            if (f.vue === "mes" && (!me || norm(t.user_interne) !== me)) return false;
            if (f.vue === "retard" && !isLate(t)) return false;
            if (formes.length && formes.indexOf(norm(t.forme)) === -1) return false;
            if (f.user) {
                if (f.user === UNASSIGNED) { if (t.user_interne) return false; }
                else if (norm(t.user_interne) !== norm(f.user)) return false;
            }
            if (f.societe && norm(t.societe) !== norm(f.societe)) return false;
            if (since && t.creeMs && t.creeMs < since) return false;
            if (q && !global) { // en global, FileMaker a déjà fait la recherche
                const hay = norm([t.societe, t.client, t.mail_expediteur, t.mail_expediteur_nom, t.telephone, t.commentaire, t.ia_resume, t.forme, t.statut, t.user_interne, t.id].join(" "));
                const phoneHit = qd.length >= 3 && digits(t.telephone).indexOf(qd) !== -1;
                if (hay.indexOf(q) === -1 && !phoneHit) return false;
            }
            const entry = hiddenEntryOf(t);
            if (entry) {
                bySender[entry] = (bySender[entry] || 0) + 1;
                if (shown.indexOf(entry) === -1) { hidden++; return false; }
            }
            return true;
        });
        return { list: list, hidden: hidden, bySender: bySender };
    });
    const filteredTickets = computed(() => filterResult.value.list);
    const hiddenCount = computed(() => filterResult.value.hidden);
    // Liste des expéditeurs masqués par défaut : nb de mails (selon la vue et
    // les filtres en cours) et affichés ou non → menu de la pastille.
    const hiddenSenderStats = computed(() => {
        const shown = filters.value.shownSenders || [];
        const by = filterResult.value.bySender;
        return HIDDEN_SENDERS.map((s) => {
            const key = norm(s);
            return { sender: s, key: key, count: by[key] || 0, shown: shown.indexOf(key) !== -1 };
        });
    });
    const toggleSender = (key) => {
        const shown = (filters.value.shownSenders || []).slice();
        const i = shown.indexOf(key);
        if (i === -1) shown.push(key);
        else shown.splice(i, 1);
        filters.value.shownSenders = shown;
    };
    const setAllSenders = (show) => {
        filters.value.shownSenders = show ? HIDDEN_LIST.slice() : [];
    };
    // Expéditeur masqué et non affiché par choix → exclu de la liste et des compteurs.
    const isMasked = (t) => {
        const entry = hiddenEntryOf(t);
        return !!entry && (filters.value.shownSenders || []).indexOf(entry) === -1;
    };

    // Compteurs (sur l'ensemble des tickets chargés, hors expéditeurs masqués).
    const counts = computed(() => {
        const me = norm(currentUser.value);
        const startDay = moment().startOf("day").valueOf();
        const base = tickets.value.filter((t) => !isMasked(t));
        const c = { aTraiter: 0, mes: 0, tous: base.length, nonAssignes: 0, today: 0, retard: 0 };
        base.forEach((t) => {
            const open = !isClosed(t.statut);
            if (open) c.aTraiter++;
            if (open && me && norm(t.user_interne) === me) c.mes++;
            if (open && !t.user_interne) c.nonAssignes++;
            if (t.creeMs && t.creeMs >= startDay) c.today++;
            if (isLate(t)) c.retard++;
        });
        return c;
    });

    // ── Actions ───────────────────────────────────────────────────────────
    const createTicket = (form, done) => {
        if (isSaving.value) return;
        const ticket = {
            societe: txt(form.societe).trim(),
            client: txt(form.client).trim(),
            telephone: txt(form.telephone).trim(),
            commentaire: txt(form.commentaire).trim(),
            forme: txt(form.forme).trim(),
            statut: txt(form.statut).trim() || DEFAULT_STATUTS[0],
            user_interne: txt(form.user_interne).trim(),
        };
        isSaving.value = true;
        engine.create_ticket(ticket, (data) => {
            isSaving.value = false;
            if (data && data.code === 0) {
                showMessage("Ticket créé.", "success");
                const rid = data.data && data.data.recordId ? String(data.data.recordId) : null;
                loadTickets({
                    silent: true,
                    then: () => {
                        let t = rid ? tickets.value.find((x) => x.recordId === rid) : null;
                        if (!t) {
                            t = tickets.value.filter((x) => norm(x.societe) === norm(ticket.societe) && norm(x.client) === norm(ticket.client))
                                .sort((a, b) => b.creeMs - a.creeMs)[0];
                        }
                        if (t) selectTicket(t);
                    },
                });
                if (done) done(true);
            } else {
                showMessage(withDetail("Échec de la création du ticket.", data), "error");
                if (done) done(false);
            }
        });
    };

    // N'envoie QUE les champs réellement modifiés + les événements à tracer.
    const updateTicket = (ticket, changes, events, okMessage, done) => {
        if (!ticket || !ticket.recordId || isSaving.value) { if (done) done(false); return; }
        const diff = {};
        Object.keys(changes || {}).forEach((k) => {
            const v = txt(changes[k]);
            if (v !== txt(ticket[k])) diff[k] = v;
        });
        if (!Object.keys(diff).length) { if (done) done(true); return; }
        isSaving.value = true;
        engine.update_ticket({
            recordId: ticket.recordId,
            id: ticket.id,
            changes: diff,
            auteur: currentUser.value,
            events: events || [],
        }, (data) => {
            isSaving.value = false;
            if (data && data.code === 0) {
                [tickets.value, serverResults.value].forEach((list) => {
                    const t = list.find((x) => x.id === ticket.id);
                    if (t) Object.assign(t, diff); // affichage immédiat
                });
                if (okMessage) showMessage(okMessage, "success");
                if (events && events.length && selectedId.value === ticket.id) loadEchanges(ticket.id, true);
                refreshTickets();
                if (done) done(true);
            } else {
                showMessage(withDetail("Échec de l'enregistrement du ticket.", data), "error");
                if (done) done(false);
            }
        });
    };

    const changeStatut = (ticket, statut) => {
        if (!ticket || txt(statut) === ticket.statut) return;
        updateTicket(ticket, { statut: statut },
            [{ type: "statut", message: "Statut : " + (ticket.statut || "—") + " → " + statut }],
            "Statut mis à jour.");
    };

    const assignTicket = (ticket, user) => {
        const u = txt(user).trim();
        if (!ticket || u === ticket.user_interne) return;
        const message = u
            ? "Pris en charge par " + u + (ticket.user_interne ? " (avant : " + ticket.user_interne + ")" : "")
            : "Ticket désassigné (avant : " + ticket.user_interne + ")";
        updateTicket(ticket, { user_interne: u }, [{ type: "assignation", message: message }],
            u ? "Ticket assigné à " + u + "." : "Ticket désassigné.");
    };

    // « Prendre en charge » : m'assigne le ticket et le passe « En cours » s'il
    // était encore ouvert.
    const takeTicket = (ticket) => {
        if (!ticket || !currentUser.value) return;
        const changes = { user_interne: currentUser.value };
        const events = [{
            type: "assignation",
            message: "Pris en charge par " + currentUser.value + (ticket.user_interne ? " (avant : " + ticket.user_interne + ")" : ""),
        }];
        const st = norm(ticket.statut);
        if (!st || st === norm(DEFAULT_STATUTS[0])) {
            changes.statut = DEFAULT_STATUTS[1];
            events.push({ type: "statut", message: "Statut : " + (ticket.statut || "—") + " → " + DEFAULT_STATUTS[1] });
        }
        updateTicket(ticket, changes, events, "Ticket pris en charge.");
    };

    // ── Actions de masse (tickets cochés dans la liste) ──────────────────
    // Même requête et mêmes événements tracés que pour un ticket seul, 3
    // tickets à la fois ; un seul message et une seule actualisation à la fin.
    // build(ticket) → { changes, events } (ou null pour l'ignorer).
    // done(idsOk, idsEchec).
    const BULK_PARALLEL = 3;
    const bulk = ref({ running: false, done: 0, total: 0 });
    const plural = (n, word) => n + " " + word + (n > 1 ? "s" : "");
    const bulkUpdate = (list, build, okLabel, done) => {
        if (bulk.value.running || isSaving.value) { if (done) done([], []); return; }
        const jobs = [];
        (list || []).forEach((t) => {
            const op = t && t.recordId ? build(t) : null;
            if (!op) return;
            const diff = {};
            Object.keys(op.changes || {}).forEach((k) => {
                const v = txt(op.changes[k]);
                if (v !== txt(t[k])) diff[k] = v;
            });
            if (Object.keys(diff).length) jobs.push({ t: t, diff: diff, events: op.events || [] });
        });
        if (!jobs.length) {
            showMessage("Rien à modifier : les tickets sélectionnés sont déjà dans cet état.", "info");
            if (done) done([], []);
            return;
        }
        const okIds = [];
        const koIds = [];
        let lastError = null;
        let next = 0;
        bulk.value = { running: true, done: 0, total: jobs.length };
        isSaving.value = true; // bloque les modifications unitaires pendant l'opération
        const finish = () => {
            bulk.value = { running: false, done: 0, total: 0 };
            isSaving.value = false;
            if (koIds.length) {
                showMessage(withDetail(plural(okIds.length, "ticket") + " modifié" + (okIds.length > 1 ? "s" : "")
                    + ", " + koIds.length + " en échec (toujours coché" + (koIds.length > 1 ? "s" : "") + ").", lastError), "error");
            } else showMessage(okLabel(okIds.length), "success");
            if (selectedId.value && okIds.indexOf(selectedId.value) !== -1) loadEchanges(selectedId.value, true);
            refreshTickets();
            if (done) done(okIds, koIds);
        };
        const launch = () => {
            if (next >= jobs.length) return;
            const j = jobs[next++];
            engine.update_ticket({
                recordId: j.t.recordId,
                id: j.t.id,
                changes: j.diff,
                auteur: currentUser.value,
                events: j.events,
            }, (data) => {
                if (data && data.code === 0) {
                    okIds.push(j.t.id);
                    [tickets.value, serverResults.value].forEach((l) => {
                        const x = l.find((y) => y.id === j.t.id);
                        if (x) Object.assign(x, j.diff); // affichage immédiat
                    });
                } else {
                    koIds.push(j.t.id);
                    lastError = data;
                }
                bulk.value.done = okIds.length + koIds.length;
                if (bulk.value.done === jobs.length) finish();
                else launch();
            });
        };
        for (let i = 0; i < Math.min(BULK_PARALLEL, jobs.length); i++) launch();
    };

    const bulkStatut = (list, statut, done) => {
        const s = txt(statut).trim();
        if (!s) return;
        bulkUpdate(list, (t) => ({
            changes: { statut: s },
            events: [{ type: "statut", message: "Statut : " + (t.statut || "—") + " → " + s + " (action groupée)" }],
        }), (n) => plural(n, "ticket") + " passé" + (n > 1 ? "s" : "") + " en « " + s + " ».", done);
    };

    const bulkAssign = (list, user, done) => {
        const u = txt(user).trim();
        bulkUpdate(list, (t) => (u === txt(t.user_interne) ? null : {
            changes: { user_interne: u },
            events: [{
                type: "assignation",
                message: (u
                    ? "Pris en charge par " + u + (t.user_interne ? " (avant : " + t.user_interne + ")" : "")
                    : "Ticket désassigné (avant : " + t.user_interne + ")") + " (action groupée)",
            }],
        }), (n) => (u
            ? plural(n, "ticket") + " assigné" + (n > 1 ? "s" : "") + " à " + u + "."
            : plural(n, "ticket") + " désassigné" + (n > 1 ? "s" : "") + "."), done);
    };

    const FIELD_LABELS = { societe: "société", client: "code client", telephone: "téléphone", forme: "forme", commentaire: "demande" };
    const saveFields = (ticket, fields, done) => {
        if (!ticket) return;
        const changed = Object.keys(FIELD_LABELS).filter((k) => fields[k] !== undefined && txt(fields[k]).trim() !== txt(ticket[k]).trim());
        if (!changed.length) { if (done) done(true); return; }
        const changes = {};
        changed.forEach((k) => { changes[k] = k === "commentaire" ? txt(fields[k]) : txt(fields[k]).trim(); });
        updateTicket(ticket, changes,
            [{ type: "modification", message: "Modifié : " + changed.map((k) => FIELD_LABELS[k]).join(", ") }],
            "Ticket enregistré.", done);
    };

    const addEchange = (ticket, type, message, done) => {
        const msg = txt(message).trim();
        if (!ticket || !msg || isSending.value) { if (done) done(false); return; }
        isSending.value = true;
        engine.add_echange({ id: ticket.id, recordId: ticket.recordId, type: type, message: msg, auteur: currentUser.value }, (data) => {
            isSending.value = false;
            if (data && data.code === 0) {
                if (selectedId.value === ticket.id) {
                    // Affichage immédiat, remplacé par la version serveur juste après.
                    echanges.value = echanges.value.concat([{
                        id: "tmp-" + Date.now(), recordId: null, ticketId: ticket.id, auteur: currentUser.value,
                        type: type, message: msg, cree: "", creeMs: Date.now(), pending: true,
                    }]);
                    loadEchanges(ticket.id, true);
                }
                refreshTickets();
                if (done) done(true);
            } else {
                showMessage(withDetail("Échec de l'ajout de l'échange.", data), "error");
                if (done) done(false);
            }
        });
    };

    // ── Affichage ─────────────────────────────────────────────────────────
    const statutColor = (statut) => {
        const s = norm(statut).replace(/\s+/g, "");
        if (!s || s === "ouvert" || s === "nouveau") return "#E53935";
        if (s === "encours") return "#FB8C00";
        if (s.indexOf("attente") !== -1) return "#5C6BC0";
        if (s === "resolu") return "#43A047";
        if (isClosed(statut)) return "#90A4AE";
        return "#78909C";
    };

    const formeIcon = (forme) => {
        const f = norm(forme);
        if (f.indexOf("entrant") !== -1) return "mdi-phone-incoming";
        if (f.indexOf("sortant") !== -1) return "mdi-phone-outgoing";
        if (f.indexOf("mail") !== -1) return "mdi-email-outline";
        if (f.indexOf("appel") !== -1 || f.indexOf("tel") !== -1) return "mdi-phone";
        return "mdi-shape-outline";
    };

    const echangeMeta = (type) => {
        const t = ECHANGE_TYPES.find((x) => x.value === type);
        if (t) return { label: t.label, icon: t.icon, color: t.color, system: false };
        const s = SYSTEM_TYPES[type];
        if (s) return { label: s.label, icon: s.icon, color: s.color, system: true };
        return { label: type || "Note", icon: "mdi-message-outline", color: "#607D8B", system: false };
    };

    const formatTs = (ms) => (ms ? moment(ms).format("DD/MM/YYYY HH:mm") : "—");
    const formatShort = (ms) => {
        if (!ms) return "—";
        return moment(ms).isSame(moment(), "year") ? moment(ms).format("DD/MM HH:mm") : moment(ms).format("DD/MM/YYYY");
    };
    const relTime = (ms) => {
        if (!ms) return "—";
        const now = nowTick.value;
        const diff = now - ms;
        if (diff < 45000) return "à l'instant";
        const min = Math.round(diff / 60000);
        if (min < 60) return "il y a " + min + " min";
        const d = moment(ms);
        if (d.isSame(moment(now), "day")) return "il y a " + Math.floor(min / 60) + " h";
        if (d.isSame(moment(now).subtract(1, "day"), "day")) return "hier " + d.format("HH:mm");
        return formatShort(ms);
    };
    const initials = (name) => {
        const parts = txt(name).trim().split(/[\s._@-]+/).filter(Boolean);
        if (!parts.length) return "?";
        return (parts[0][0] + (parts.length > 1 ? parts[1][0] : (parts[0][1] || ""))).toUpperCase();
    };
    const telHref = (tel) => {
        const t = txt(tel).trim();
        if (!t) return null;
        return "tel:" + (t.charAt(0) === "+" ? "+" : "") + digits(t);
    };

    // ── Actualisation automatique ─────────────────────────────────────────
    let timer = null;
    const autoTick = () => {
        nowTick.value = Date.now();
        if (typeof document !== "undefined" && document.hidden) return; // onglet caché : pas de charge FM
        refreshTickets();
        if (selectedId.value) loadEchanges(selectedId.value, true);
    };
    const refreshNow = () => {
        nowTick.value = Date.now();
        loadTickets({ silent: tickets.value.length > 0 });
        if (selectedId.value) loadEchanges(selectedId.value, true);
    };
    const onVisibility = () => { if (typeof document !== "undefined" && !document.hidden) autoTick(); };

    onMounted(() => {
        loadTickets();
        loadClients(false);
        timer = setInterval(autoTick, AUTO_REFRESH_MS);
        if (typeof document !== "undefined") document.addEventListener("visibilitychange", onVisibility);
    });
    onBeforeUnmount(() => {
        if (searchTimer) clearTimeout(searchTimer);
        if (timer) clearInterval(timer);
        timer = null;
        if (typeof document !== "undefined") document.removeEventListener("visibilitychange", onVisibility);
    });

    return {
        // état
        currentUser, tickets, isLoading, isRefreshing, loadError, lastSync, newIds,
        clients, isClientsLoading, clientsInfo,
        selectedId, selectedTicket, selectedClient, echanges, isEchangesLoading, isSaving, isSending,
        snackbar, filters, filteredTickets, counts, hiddenCount, hiddenSenderStats, toggleSender, setAllSenders,
        searchActive, isSearching, serverResults,
        statutOptions, formeOptions, userOptions, societeOptions,
        // actions
        loadTickets, refreshTickets, refreshNow, loadClients, loadEchanges,
        selectTicket, closeTicket, resetFilters, showMessage,
        createTicket, changeStatut, assignTicket, takeTicket, saveFields, addEchange,
        bulk, bulkStatut, bulkAssign,
        // affichage
        isClosed, isLate, ageInfo, shortId, statutColor, formeIcon, echangeMeta, formatTs, formatShort, relTime, initials, telHref,
    };
}
